/**
 * Mapa Leaflet + OpenStreetMap con un pin arrastrable = el centro de la
 * geovalla. Sin assets de imagen (usa un divIcon con CSS) para no pelearse
 * con el bundler ni el CSP; tiles directos de OSM (sin API key).
 *
 * El pin es la fuente de verdad de las coordenadas: al arrastrarlo o al hacer
 * clic en el mapa, se emite (lat, lng). Los tiles solo cargan online — es una
 * pantalla de admin, no el flujo offline del trabajador.
 */
import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Pin de marca, en CSS: gota verde con sombra. Sin PNG que empaquetar.
const PIN = L.divIcon({
  className: '',
  html:
    '<div style="width:26px;height:26px;border-radius:50% 50% 50% 0;' +
    'background:var(--brand,#0E6B45);transform:rotate(-45deg);' +
    'border:2.5px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.4)"></div>',
  iconSize: [26, 26],
  iconAnchor: [13, 24],
});

export function LocationMap({
  latitude,
  longitude,
  radius,
  onPick,
}: {
  latitude?: number;
  longitude?: number;
  radius?: number;
  onPick: (lat: number, lng: number) => void;
}) {
  const el = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const marker = useRef<L.Marker | null>(null);
  const circle = useRef<L.Circle | null>(null);
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;

  const hasPoint = latitude != null && longitude != null;
  const center: L.LatLngExpression = hasPoint
    ? [latitude as number, longitude as number]
    : [40.4168, -3.7038]; // España como encuadre por defecto

  // Montaje (una vez).
  useEffect(() => {
    if (!el.current || map.current) return;
    const m = L.map(el.current, { attributionControl: true }).setView(center, hasPoint ? 16 : 5);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap',
    }).addTo(m);

    // Clic en el mapa → mueve el pin ahí.
    m.on('click', (e: L.LeafletMouseEvent) => onPickRef.current(+e.latlng.lat.toFixed(7), +e.latlng.lng.toFixed(7)));
    map.current = m;

    // Leaflet mide mal el contenedor si arranca oculto (dentro de un form que
    // se despliega): forzar un recálculo tras el primer layout.
    setTimeout(() => m.invalidateSize(), 60);

    return () => { m.remove(); map.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pin + círculo de radio, reactivos a las props.
  useEffect(() => {
    const m = map.current;
    if (!m) return;

    if (hasPoint) {
      const pos: L.LatLngExpression = [latitude as number, longitude as number];
      if (!marker.current) {
        marker.current = L.marker(pos, { icon: PIN, draggable: true }).addTo(m);
        marker.current.on('dragend', () => {
          const p = marker.current!.getLatLng();
          onPickRef.current(+p.lat.toFixed(7), +p.lng.toFixed(7));
        });
      } else {
        marker.current.setLatLng(pos);
      }
      m.setView(pos, Math.max(m.getZoom(), 15));

      if (radius && radius > 0) {
        if (!circle.current) {
          circle.current = L.circle(pos, { radius, color: 'var(--brand,#0E6B45)', weight: 1.5, fillOpacity: 0.08 }).addTo(m);
        } else {
          circle.current.setLatLng(pos).setRadius(radius);
        }
      } else if (circle.current) {
        circle.current.remove(); circle.current = null;
      }
    } else if (marker.current) {
      marker.current.remove(); marker.current = null;
      if (circle.current) { circle.current.remove(); circle.current = null; }
    }
  }, [latitude, longitude, radius, hasPoint]);

  return (
    <div
      ref={el}
      className="w-full h-[260px] rounded-xl overflow-hidden border border-line-strong"
      style={{ background: 'var(--surface-2)' }}
      aria-label="Mapa para fijar la ubicación del centro"
    />
  );
}
